/* ===========================================================================
 * kaa-email-validator.js
 * ---------------------------------------------------------------------------
 * Client-side email validation for SD Labs (superdeodorant.com).
 *
 * Three checks, in order:
 *   1. Typo suggestion  — Levenshtein against known-good domains + TLDs.
 *                         Asks "did you mean gmail.com?" before submit.
 *   2. Disposable block — hard reject against blocklist
 *                         (loaded async from /assets/kaa-disposable.json).
 *   3. MX existence     — DNS-over-HTTPS query to Cloudflare.
 *                         Blocks gmsil.com, hotmail.con, gmail.vom etc.
 *
 * Hooks every input[type=email] on the page AND intercepts Klaviyo form
 * submits where possible.
 *
 * No dependencies. Safe to load twice.
 * =========================================================================== */

(function () {
  "use strict";
  if (window.__KAA_EMAIL_VALIDATOR_LOADED__) return;
  window.__KAA_EMAIL_VALIDATOR_LOADED__ = true;

  // ---------- config -----------------------------------------------------

  var KNOWN_DOMAINS = [
    // Top consumer mail providers (for typo suggestion)
    "gmail.com", "yahoo.com", "hotmail.com", "outlook.com", "icloud.com",
    "aol.com", "live.com", "msn.com", "me.com", "mac.com", "comcast.net",
    "sbcglobal.net", "verizon.net", "att.net", "cox.net", "earthlink.net",
    "bellsouth.net", "charter.net", "rogers.com", "sympatico.ca", "shaw.ca",
    "telus.net", "videotron.ca", "hotmail.co.uk", "yahoo.co.uk",
    "yahoo.ca", "hotmail.ca", "gmail.co.uk", "outlook.co.uk",
    "proton.me", "protonmail.com", "pm.me", "ymail.com", "rocketmail.com",
    "googlemail.com", "outlook.fr", "hotmail.fr", "yahoo.fr", "orange.fr",
    "btinternet.com", "virginmedia.com", "sky.com", "hotmail.it", "yahoo.it",
    "web.de", "gmx.de", "t-online.de", "hotmail.de", "yahoo.de",
  ];
  var KNOWN_TLDS = [
    "com", "net", "org", "co.uk", "ca", "io", "me", "info", "us",
    "fr", "de", "it", "es", "nl", "se", "no", "fi", "dk", "au", "nz",
    "com.au", "co.nz", "ie", "be", "ch", "at", "ru", "pl", "cz",
    "edu", "gov", "biz", "xyz", "online", "store",
  ];
  var DOH_URL = "https://cloudflare-dns.com/dns-query";
  var DOH_TIMEOUT_MS = 3500;
  var MAX_DISTANCE_DOMAIN = 2;
  var MAX_DISTANCE_TLD = 2;
  var BLOCKLIST_PATH_CANDIDATES = [
    "/kaa-disposable.json",
    window.__KAA_DISPOSABLE_URL || "",
    "/apps/kaa/disposable.json",
  ];

  var disposableSet = null;
  var disposableFetching = null;
  var mxCache = {};        // domain -> Promise<boolean>
  var suggestionSeen = {}; // input element -> last suggestion shown

  // ---------- utils ------------------------------------------------------

  function levenshtein(a, b) {
    a = a.toLowerCase(); b = b.toLowerCase();
    if (a === b) return 0;
    var al = a.length, bl = b.length;
    if (!al) return bl;
    if (!bl) return al;
    var prev = new Array(bl + 1), curr = new Array(bl + 1);
    for (var j = 0; j <= bl; j++) prev[j] = j;
    for (var i = 1; i <= al; i++) {
      curr[0] = i;
      for (var k = 1; k <= bl; k++) {
        curr[k] = Math.min(
          curr[k - 1] + 1,
          prev[k] + 1,
          prev[k - 1] + (a.charAt(i - 1) === b.charAt(k - 1) ? 0 : 1)
        );
      }
      var tmp = prev; prev = curr; curr = tmp;
    }
    return prev[bl];
  }

  function splitEmail(email) {
    if (!email) return null;
    var at = email.lastIndexOf("@");
    if (at <= 0 || at === email.length - 1) return null;
    var domain = email.slice(at + 1).toLowerCase();
    var dot = domain.indexOf(".");
    if (dot <= 0) return null;
    var sld = domain.slice(0, dot);
    var tld = domain.slice(dot + 1);
    return {
      address: email,
      local: email.slice(0, at),
      domain: domain,
      sld: sld,
      tld: tld,
    };
  }

  function closestMatch(value, candidates, maxDist) {
    var best = null, bestDist = maxDist + 1;
    for (var i = 0; i < candidates.length; i++) {
      var d = levenshtein(value, candidates[i]);
      if (d < bestDist) { bestDist = d; best = candidates[i]; }
    }
    if (!best || bestDist === 0 || bestDist > maxDist) return null;
    return best;
  }

  function suggestDomain(parsed) {
    if (!parsed) return null;
    if (KNOWN_DOMAINS.indexOf(parsed.domain) !== -1) return null;

    // 1. full-domain Levenshtein
    var full = closestMatch(parsed.domain, KNOWN_DOMAINS, MAX_DISTANCE_DOMAIN);
    if (full) return parsed.local + "@" + full;

    // 2. TLD-only Levenshtein (handles gmail.con, gmail.clm, gmail.vom)
    if (KNOWN_TLDS.indexOf(parsed.tld) === -1) {
      var tld = closestMatch(parsed.tld, KNOWN_TLDS, MAX_DISTANCE_TLD);
      if (tld) return parsed.local + "@" + parsed.sld + "." + tld;
    }
    return null;
  }

  // ---------- blocklist --------------------------------------------------

  function loadBlocklist() {
    if (disposableSet) return Promise.resolve(disposableSet);
    if (disposableFetching) return disposableFetching;
    disposableFetching = (function tryNext(idx) {
      if (idx >= BLOCKLIST_PATH_CANDIDATES.length) {
        disposableSet = new Set();
        return Promise.resolve(disposableSet);
      }
      var url = BLOCKLIST_PATH_CANDIDATES[idx];
      if (!url) return tryNext(idx + 1);
      return fetch(url, { credentials: "omit", cache: "force-cache" })
        .then(function (r) {
          if (!r.ok) throw new Error("blocklist fetch failed");
          return r.json();
        })
        .then(function (list) {
          disposableSet = new Set(list.map(function (s) { return String(s).toLowerCase(); }));
          return disposableSet;
        })
        .catch(function () { return tryNext(idx + 1); });
    })(0);
    return disposableFetching;
  }

  function isDisposable(domain) {
    return loadBlocklist().then(function (set) { return set.has(domain); });
  }

  // ---------- MX via DoH -------------------------------------------------

  function hasMX(domain) {
    if (domain in mxCache) return mxCache[domain];
    var p = (function () {
      return new Promise(function (resolve) {
        var done = false;
        var t = setTimeout(function () {
          if (done) return;
          done = true;
          resolve(true); // fail-open on timeout (do not block valid users on flaky network)
        }, DOH_TIMEOUT_MS);
        fetch(DOH_URL + "?name=" + encodeURIComponent(domain) + "&type=MX",
              { headers: { accept: "application/dns-json" }, credentials: "omit" })
          .then(function (r) { return r.ok ? r.json() : null; })
          .then(function (j) {
            if (done) return;
            done = true;
            clearTimeout(t);
            if (!j) return resolve(true);
            // Status 0 = NOERROR. Answer present = has MX (or CNAME chain to MX).
            if (j.Status === 0 && j.Answer && j.Answer.length) return resolve(true);
            // NXDOMAIN or no MX: fall back to A record check
            fetch(DOH_URL + "?name=" + encodeURIComponent(domain) + "&type=A",
                  { headers: { accept: "application/dns-json" }, credentials: "omit" })
              .then(function (r) { return r.ok ? r.json() : null; })
              .then(function (j2) {
                if (!j2) return resolve(true);
                if (j2.Status === 0 && j2.Answer && j2.Answer.length) return resolve(true);
                resolve(false);
              })
              .catch(function () { resolve(true); });
          })
          .catch(function () {
            if (done) return;
            done = true;
            clearTimeout(t);
            resolve(true);
          });
      });
    })();
    mxCache[domain] = p;
    return p;
  }

  // ---------- UI ---------------------------------------------------------

  function ensureHintNode(input) {
    var id = "kaa-email-hint-" + (input.dataset.kaaId || (input.dataset.kaaId = String(Math.random()).slice(2, 10)));
    var node = document.getElementById(id);
    if (!node) {
      node = document.createElement("div");
      node.id = id;
      node.className = "kaa-email-hint";
      node.style.cssText = "font: 12px/1.3 system-ui,-apple-system,Segoe UI,Roboto,Arial,sans-serif;"
        + "color:#b04a00;margin:4px 0 0;padding:6px 8px;background:#fff8ef;"
        + "border:1px solid #f0d6b4;border-radius:4px;display:none;";
      input.parentNode && input.parentNode.insertBefore(node, input.nextSibling);
    }
    return node;
  }

  function showSuggestion(input, suggestion) {
    if (suggestionSeen[input.dataset.kaaId] === suggestion) return;
    suggestionSeen[input.dataset.kaaId] = suggestion;
    var hint = ensureHintNode(input);
    hint.innerHTML = "";
    var text = document.createTextNode("Did you mean ");
    var btn = document.createElement("button");
    btn.type = "button";
    btn.textContent = suggestion + "?";
    btn.style.cssText = "background:none;border:none;color:#0a5ab5;"
      + "text-decoration:underline;cursor:pointer;font:inherit;padding:0;";
    btn.addEventListener("click", function (e) {
      e.preventDefault();
      input.value = suggestion;
      hint.style.display = "none";
      suggestionSeen[input.dataset.kaaId] = null;
      input.dispatchEvent(new Event("input", { bubbles: true }));
      input.dispatchEvent(new Event("change", { bubbles: true }));
    });
    hint.appendChild(text);
    hint.appendChild(btn);
    hint.style.display = "block";
  }

  function showError(input, message) {
    var hint = ensureHintNode(input);
    hint.textContent = message;
    hint.style.color = "#a00";
    hint.style.borderColor = "#f4bcbc";
    hint.style.background = "#fff0f0";
    hint.style.display = "block";
  }

  function clearHint(input) {
    var hint = input && input.parentNode
      ? input.parentNode.querySelector(".kaa-email-hint")
      : null;
    if (hint) hint.style.display = "none";
  }

  // ---------- core check -------------------------------------------------

  function validate(email) {
    var parsed = splitEmail(email);
    if (!parsed) return Promise.resolve({ ok: false, reason: "malformed" });

    return isDisposable(parsed.domain).then(function (disp) {
      if (disp) return { ok: false, reason: "disposable" };
      var suggestion = suggestDomain(parsed);
      return hasMX(parsed.domain).then(function (mx) {
        if (!mx) return { ok: false, reason: "no_mx", suggestion: suggestion };
        return { ok: true, suggestion: suggestion };
      });
    });
  }

  // ---------- DOM wiring -------------------------------------------------

  function onBlur(input) {
    var v = (input.value || "").trim();
    if (!v || v.indexOf("@") === -1) return;
    var parsed = splitEmail(v);
    if (!parsed) return;
    var suggestion = suggestDomain(parsed);
    if (suggestion) showSuggestion(input, suggestion);
    else clearHint(input);
  }

  function interceptForm(form) {
    if (form.__kaaEmailBound) return;
    form.__kaaEmailBound = true;

    form.addEventListener("submit", function (e) {
      var inputs = form.querySelectorAll('input[type=email], input[name*=email i]');
      if (!inputs.length) return;

      // If any input has a pending async check, block and run validation.
      // We always prevent submit first, then resubmit if all pass.
      var allValid = [];
      for (var i = 0; i < inputs.length; i++) {
        var v = (inputs[i].value || "").trim();
        if (!v) continue;
        allValid.push({ input: inputs[i], value: v });
      }
      if (!allValid.length) return;

      // If already validated OK in a previous pass this session, let through
      if (form.__kaaEmailPassed) return;

      e.preventDefault();
      e.stopPropagation();

      Promise.all(allValid.map(function (x) {
        return validate(x.value).then(function (res) { return { x: x, res: res }; });
      })).then(function (results) {
        var firstBad = null;
        for (var i = 0; i < results.length; i++) {
          if (!results[i].res.ok) { firstBad = results[i]; break; }
        }
        if (firstBad) {
          var r = firstBad.res;
          var msg;
          if (r.reason === "disposable") msg = "This email provider is not accepted. Please use a personal or work email.";
          else if (r.reason === "no_mx") msg = r.suggestion
            ? "This email address looks wrong. Did you mean " + r.suggestion + "?"
            : "This email address does not exist.";
          else msg = "Please enter a valid email address.";
          showError(firstBad.x.input, msg);
          firstBad.x.input.focus();
          return;
        }
        // All good. Apply any suggested correction quietly? No — we only
        // suggest, we do not auto-rewrite. Re-submit the form.
        // Use requestSubmit() so the re-submission fires a real, cancelable
        // 'submit' event — this lets JS-handled forms (e.g. the Klaviyo
        // opt-in) run their own submit listeners. Native form.submit() skips
        // all listeners, which silently breaks those forms. Fallback to
        // submit() only where requestSubmit is unavailable.
        form.__kaaEmailPassed = true;
        if (typeof form.requestSubmit === "function") form.requestSubmit();
        else form.submit();
      });
    }, true);
  }

  function scan(root) {
    var inputs = (root || document).querySelectorAll('input[type=email], input[name*=email i]');
    for (var i = 0; i < inputs.length; i++) {
      var inp = inputs[i];
      if (inp.__kaaEmailBound) continue;
      inp.__kaaEmailBound = true;
      inp.addEventListener("blur", (function (x) { return function () { onBlur(x); }; })(inp));
      inp.addEventListener("input", (function (x) { return function () { clearHint(x); }; })(inp));
      var f = inp.form;
      if (f) interceptForm(f);
    }
  }

  // ---------- Klaviyo hook ----------------------------------------------

  function hookKlaviyo() {
    // Klaviyo forms dispatch 'klaviyoForms' on document for lifecycle events.
    // We cannot prevent Klaviyo's internal submit from outside its iframe,
    // but we CAN validate on the wrapping form if Klaviyo rendered as
    // native HTML (embedded, not iframe). For iframe popups, the in-iframe
    // validation must be configured in Klaviyo Form Builder (see docs).
    document.addEventListener("klaviyoForms", function (e) {
      if (!e.detail) return;
      // On 'embedForm', scan the new DOM so we can catch email fields Klaviyo
      // injected into the page.
      if (e.detail.type === "embedForm" || e.detail.type === "open") {
        setTimeout(scan, 200);
      }
    });
  }

  // ---------- boot -------------------------------------------------------

  function boot() {
    loadBlocklist(); // start fetch early
    scan(document);
    hookKlaviyo();
    // Catch dynamically inserted forms
    var mo = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var m = mutations[i];
        for (var j = 0; j < m.addedNodes.length; j++) {
          var n = m.addedNodes[j];
          if (n.nodeType === 1) scan(n);
        }
      }
    });
    mo.observe(document.documentElement, { childList: true, subtree: true });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }

  // Expose for testing
  window.KaaEmailValidator = {
    validate: validate,
    suggestDomain: function (email) {
      var p = splitEmail(email);
      return p ? suggestDomain(p) : null;
    },
    _levenshtein: levenshtein,
  };
})();
